import React from 'react';
import ReactDOM from 'react-dom';
import { cn } from '@/lib/utils';
import { Minus, Maximize2, Minimize2, X, GripHorizontal, Pin, ExternalLink, Expand } from 'lucide-react';
import AiHandledBanner from '@/components/AiHandledBanner';

const PIN_KEY_PREFIX = 'pw_panel_pinned:';
const MIN_KEY_PREFIX = 'pw_panel_min:';

function readFlag(prefix, id, fallback = false) {
  if (!id) return fallback;
  try { return window.localStorage.getItem(prefix + id) === '1'; }
  catch { return fallback; }
}
function writeFlag(prefix, id, value) {
  if (!id) return;
  try {
    if (value) window.localStorage.setItem(prefix + id, '1');
    else window.localStorage.removeItem(prefix + id);
  } catch { /* localStorage blocked */ }
}

/**
 * PanelFrame — reusable "plate / window" chrome used inside the workspace.
 * Mimics Adobe/Microsoft panel headers with a drag handle, controls, and an actions slot.
 *
 * iter55f91 — Pin / Minimize / Pop-out buttons are now functional:
 *   • Pin      → sticky flag persisted in localStorage under `panelId`;
 *                calls `onPinChange(next)` if the parent wants to react
 *                (e.g., lock the panel visible when layout switches).
 *   • Minimize → collapses the body to a titlebar-only strip. Local
 *                state, click the button again to restore.
 *   • Pop out  → opens a dedicated `/panel-window/{panelId}` route in
 *                a new browser window at 720×860, useful for writers
 *                on two monitors who want the Character Bible open
 *                on the side while they draft.
 *   • Maximize (existing) → still routed via `onMaximize` when provided.
 */
export function PanelFrame({
  title, icon: Icon, badge, color = 'primary',
  onClose, onMaximize, maximized, actions, children, className, testId,
  panelId,
  onPinChange,
  aiHint,       // panel key for AiHandledBanner (e.g. 'editor', 'bible')
}) {
  const [minimized,   setMinimized]   = React.useState(() => readFlag(MIN_KEY_PREFIX, panelId));
  const [pinned,      setPinned]      = React.useState(() => readFlag(PIN_KEY_PREFIX, panelId));
  // 'normal' | 'half' | 'full'  — overlay expand mode
  const [expandMode,  setExpandMode]  = React.useState('normal');

  const onEnter = React.useCallback(() => {
    if (!panelId) return;
    import('@/lib/analytics')
      .then(({ trackPanelFocus }) => trackPanelFocus(panelId))
      .catch(() => { /* silent */ });
  }, [panelId]);

  const togglePin = React.useCallback(() => {
    const next = !pinned;
    setPinned(next);
    writeFlag(PIN_KEY_PREFIX, panelId, next);
    onPinChange?.(next);
  }, [pinned, panelId, onPinChange]);

  const toggleMinimize = React.useCallback(() => {
    setMinimized((prev) => {
      const next = !prev;
      writeFlag(MIN_KEY_PREFIX, panelId, next);
      return next;
    });
  }, [panelId]);

  const cycleExpand = React.useCallback(() => {
    setExpandMode((prev) => prev === 'normal' ? 'half' : prev === 'half' ? 'full' : 'normal');
  }, []);

  const popOut = React.useCallback(() => {
    if (!panelId) return;
    const url = `/panel-window/${encodeURIComponent(panelId)}`;
    const feats = 'width=720,height=860,menubar=no,toolbar=no,location=no';
    try {
      const w = window.open(url, `pw_panel_${panelId}`, feats);
      if (w) w.focus();
    } catch { /* popup blocked */ }
  }, [panelId]);

  // Shared panel chrome (header + body) rendered in both normal and overlay modes
  const panelChrome = (isOverlay) => (
    <div
      className={cn(
        'relative flex h-full w-full flex-col overflow-hidden border-l border-r border-panel-border bg-panel panel-bg',
        isOverlay && 'rounded-lg border border-panel-border shadow-2xl',
        !isOverlay && className,
      )}
      data-testid={testId}
      data-panel={panelId || undefined}
      data-pinned={pinned || undefined}
      data-minimized={minimized || undefined}
      onMouseEnter={panelId ? onEnter : undefined}
      onFocusCapture={panelId ? onEnter : undefined}
    >
      <header
        className="flex h-8 shrink-0 items-center gap-0 overflow-hidden border-b border-panel-border panel-header-bg pl-1.5 pr-1 cursor-grab"
        onDoubleClick={(!isOverlay && panelId) ? toggleMinimize : undefined}
        title={(!isOverlay && panelId) ? 'Double-click to minimize/restore' : undefined}
      >
        {/* ── Left: icon + title + badge — badge is shrink-0 so it never disappears ── */}
        <div className="flex items-center gap-1.5 min-w-0 mr-1">
          <GripHorizontal className="h-3 w-3 shrink-0 text-muted-foreground/60" />
          {Icon && (
            <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded-sm', `text-${color}`)}>
              <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
          )}
          <span className="truncate text-[11.5px] font-semibold uppercase tracking-wider text-foreground/85">{title}</span>
          {badge && (
            <span className="ml-1 shrink-0 rounded-sm bg-secondary px-1.5 py-0.5 font-mono-stat text-[10px] text-muted-foreground">{badge}</span>
          )}
          {pinned && !isOverlay && (
            <span className="ml-1 shrink-0 rounded-sm bg-primary/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary" data-testid={panelId ? `panel-pinned-badge-${panelId}` : undefined}>Pinned</span>
          )}
        </div>

        {/* ── Actions slot — clips before the fixed controls ever move ── */}
        {actions && (
          <div className="flex min-w-0 items-center overflow-hidden">
            {actions}
          </div>
        )}

        {/* ── Fixed controls — always visible, pushed flush-right ── */}
        <div className="ml-auto flex shrink-0 items-center pl-0.5">
          <span className="mx-0.5 h-4 w-px shrink-0 bg-panel-border" />

          {/* Pin — only in normal (non-overlay) mode */}
          {!isOverlay && (
            <button
              onClick={togglePin}
              aria-pressed={pinned}
              title={pinned ? 'Unpin panel' : 'Pin panel'}
              data-testid={panelId ? `panel-pin-${panelId}` : 'panel-pin'}
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-sm hover:bg-secondary hover:text-foreground',
                pinned ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <Pin className="h-3 w-3" />
            </button>
          )}

          {/* Minimize — only in normal mode */}
          {!isOverlay && (
            <button
              onClick={toggleMinimize}
              aria-pressed={minimized}
              title={minimized ? 'Restore panel' : 'Minimize panel'}
              data-testid={panelId ? `panel-minimize-${panelId}` : 'panel-minimize'}
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-sm hover:bg-secondary hover:text-foreground',
                minimized ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <Minus className="h-3 w-3" />
            </button>
          )}

          {/* Expand / cycle (half → full → normal) */}
          <button
            onClick={cycleExpand}
            title={
              expandMode === 'normal' ? 'Expand to half-screen overlay' :
              expandMode === 'half'   ? 'Expand to full-screen overlay' :
                                       'Restore to panel'
            }
            data-testid={panelId ? `panel-expand-${panelId}` : 'panel-expand'}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-sm hover:bg-secondary hover:text-foreground',
              expandMode !== 'normal' ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {expandMode === 'full'
              ? <Minimize2 className="h-3 w-3" />
              : <Expand className="h-3 w-3" />
            }
          </button>

          {/* Pop-out (new window) — normal mode only, if no onMaximize */}
          {!isOverlay && !onMaximize && (
            <button
              onClick={popOut}
              title="Pop out in a new window"
              data-testid={panelId ? `panel-popout-${panelId}` : 'panel-popout'}
              className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              disabled={!panelId}
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          )}

          {/* Legacy onMaximize */}
          {!isOverlay && onMaximize && (
            <button
              onClick={onMaximize}
              title={maximized ? 'Restore panel width' : 'Expand to half workspace'}
              aria-pressed={!!maximized}
              data-testid="panel-toggle-maximize"
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-sm hover:bg-secondary hover:text-foreground',
                maximized ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              {maximized ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            </button>
          )}

          {/* Close */}
          <button
            onClick={isOverlay ? () => setExpandMode('normal') : onClose}
            title={isOverlay ? 'Close overlay' : 'Close'}
            data-testid={panelId ? `panel-close-${panelId}` : 'panel-close'}
            className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-destructive hover:text-destructive-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </header>

      {/* Body */}
      {(!minimized || isOverlay) && (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {aiHint && (
            <div className="shrink-0 px-3 pt-2">
              <AiHandledBanner panelKey={aiHint} />
            </div>
          )}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">{children}</div>
        </div>
      )}
    </div>
  );

  // ── Overlay mode ─────────────────────────────────────────────────────────
  if (expandMode === 'half' || expandMode === 'full') {
    return (
      <>
        {/* Normal panel (collapsed/minimized shell stays in layout) */}
        <div className={cn('relative flex h-full w-full flex-col overflow-hidden border-l border-r border-panel-border bg-panel panel-bg', className)}
             data-panel={panelId || undefined}>
          <header className="flex h-8 shrink-0 items-center justify-between border-b border-panel-border panel-header-bg pl-1.5 pr-1 opacity-50 select-none">
            <div className="flex items-center gap-1.5 min-w-0">
              <GripHorizontal className="h-3 w-3 shrink-0 text-muted-foreground/40" />
              {Icon && <span className={cn('flex h-4 w-4 items-center justify-center rounded-sm', `text-${color}`)}><Icon className="h-3.5 w-3.5" /></span>}
              <span className="truncate text-[11.5px] font-semibold uppercase tracking-wider text-foreground/50">{title}</span>
            </div>
            <div className="flex items-center gap-0.5 pr-1">
              <button onClick={cycleExpand} className="flex h-6 w-6 items-center justify-center rounded-sm text-primary hover:bg-secondary" title="Restore panel">
                <Minimize2 className="h-3 w-3" />
              </button>
            </div>
          </header>
        </div>

        {/* Portal overlay */}
        {ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center"
            data-testid={`panel-overlay-${panelId}`}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setExpandMode('normal')}
              aria-hidden="true"
            />
            {/* Panel sheet */}
            <div
              className={cn(
                'relative z-10 flex flex-col overflow-hidden rounded-xl border border-panel-border bg-panel shadow-2xl',
                expandMode === 'half' ? 'w-[72vw] h-[72vh]' : 'w-[95vw] h-[92vh]',
              )}
              style={{ transition: 'width 250ms ease, height 250ms ease' }}
            >
              {panelChrome(true)}
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  }

  // ── Normal (in-layout) mode ───────────────────────────────────────────────
  return panelChrome(false);
}
